import { NavLink, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { languageNames } from '../i18n/translations';
import { api } from '../lib/api';
import { 
  House, 
  FileText, 
  Folder,
  Envelope,
  Scan,
  FlowArrow,
  ClockCounterClockwise, 
  Gear, 
  Users, 
  SignOut
} from '@phosphor-icons/react';

export const Layout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const [workflowCount, setWorkflowCount] = useState(0);

  // Fetch workflow count for badge
  useEffect(() => {
    const fetchWorkflowCount = async () => {
      if (user?.role === 'approver' || user?.role === 'admin') {
        try {
          const res = await api.getWorkflowCount();
          setWorkflowCount(res.data.count || 0);
        } catch (err) {
          console.error('Failed to fetch workflow count:', err);
        }
      }
    };
    fetchWorkflowCount();
    // Refresh every 60 seconds
    const interval = setInterval(fetchWorkflowCount, 60000);
    return () => clearInterval(interval);
  }, [user?.role]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', icon: House, label: t('nav.dashboard') },
    { path: '/documents', icon: FileText, label: t('nav.documents') },
    { path: '/folders', icon: Folder, label: t('nav.folders') },
    { path: '/emails', icon: Envelope, label: t('nav.emails') },
    { path: '/scanner', icon: Scan, label: t('nav.scanner') },
    { path: '/workflow', icon: FlowArrow, label: t('nav.workflow'), badge: workflowCount },
    { path: '/audit-log', icon: ClockCounterClockwise, label: t('nav.auditLog') },
    { path: '/settings', icon: Gear, label: t('nav.settings') },
  ];

  if (isAdmin) {
    navItems.push({ path: '/admin', icon: Users, label: t('nav.admin') });
  }

  return (
    <div className="flex min-h-screen bg-[#F4F4F5]">
      {/* Sidebar */}
      <aside className="sidebar flex flex-col">
        {/* Logo */}
        <div className="p-4 border-b border-[#E4E4E7]">
          <div className="flex items-center justify-center">
            <img 
              src="/arco-logo.png" 
              alt="ARCO" 
              className="h-12 object-contain"
            />
          </div>
        </div>

        {/* User Info */}
        <div className="px-6 py-4 border-b border-[#E4E4E7]">
          <p className="text-sm font-medium text-[#09090B] truncate">
            {user?.first_name} {user?.last_name}
          </p>
          <p className="text-xs text-[#52525B] truncate">{user?.client_name}</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `sidebar-nav-item ${isActive ? 'active' : ''}`
              }
              data-testid={`nav-${item.path.slice(1)}`}
            >
              <item.icon size={20} />
              <span className="text-sm font-medium">{item.label}</span>
              {item.badge > 0 && (
                <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {item.badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Language Switcher */}
        <div className="px-4 py-3 border-t border-[#E4E4E7]">
          <div className="lang-switcher">
            {Object.entries(languageNames).map(([code]) => (
              <button
                key={code}
                onClick={() => changeLanguage(code)}
                className={`lang-btn ${language === code ? 'active' : ''}`}
                data-testid={`sidebar-lang-${code}`}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Logout */}
        <div className="p-4 border-t border-[#E4E4E7]">
          <button
            onClick={handleLogout}
            className="sidebar-nav-item w-full justify-start text-[#E32636] hover:bg-[#FFEBEE]"
            data-testid="logout-btn"
          >
            <SignOut size={20} />
            <span className="text-sm font-medium">{t('nav.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        {children}
      </main>
    </div>
  );
};
